@file:Suppress("MagicNumber")

package com.nonopn.skydome.common.app.pages.pdf

import com.nonopn.skydome.common.app.utils.fmt
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.cos

/**
 * Generates SVG map fragments for PDF reports.
 * Uses equirectangular projection — accurate for areas < 5 km.
 */
object SvgMapBuilder {

    private const val KM_PER_DEG_LAT = 111.32

    data class ImpactDot(
        val lat: Double,
        val lon: Double,
        val isPositive: Boolean,
        val amplitudeKa: Double = 0.0
    )

    private fun toPixel(
        cLat: Double, cLon: Double,
        lat: Double, lon: Double,
        cx: Double, cy: Double, scale: Double
    ): Pair<Double, Double> {
        val dy = (lat - cLat) * KM_PER_DEG_LAT
        val dx = (lon - cLon) * KM_PER_DEG_LAT * cos(cLat * PI / 180.0)
        return (cx + dx * scale) to (cy - dy * scale)
    }

    private fun Double.s(): String = fmt(1)

    /**
     * Impact map with 1 km / 2 km concentric rings.
     * Blue = CG- (negative), Red = CG+ (positive).
     */
    fun impactMap(
        centerLat: Double,
        centerLon: Double,
        impacts: List<ImpactDot>,
        less1Km: Int,
        less2Km: Int,
        size: Int = 460,
        lang: PdfOutputLang = PdfOutputLang.FR
    ): String {
        val cx = size / 2.0
        val cy = size / 2.0
        val labels = PdfTranslations.get(lang)
        val viewKm = 2.6
        val sc = cx / viewKm
        return buildString {
            append("<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 $size $size\" ")
            append("style=\"width:100%;height:auto;aspect-ratio:1/1;display:block;margin:14px 0\">")
            append("<rect width=\"$size\" height=\"$size\" fill=\"#FAFBFC\" rx=\"10\"/>")
            append(MapTileUtils.svgTileImages(centerLat, centerLon, viewKm, size))
            // 2 km ring
            append("<circle cx=\"${cx.s()}\" cy=\"${cy.s()}\" r=\"${(sc * 2).s()}\" fill=\"rgba(245,168,0,0.05)\" stroke=\"#F5A800\" stroke-width=\"1.5\" stroke-dasharray=\"8,4\"/>")
            append("<text x=\"${(cx + sc * 2 + 5).s()}\" y=\"${(cy - 5).s()}\" font-size=\"11\" fill=\"#F5A800\" font-weight=\"700\" font-family=\"Inter,sans-serif\">2 km</text>")
            // 1 km ring
            append("<circle cx=\"${cx.s()}\" cy=\"${cy.s()}\" r=\"${sc.s()}\" fill=\"rgba(230,59,46,0.05)\" stroke=\"#E63B2E\" stroke-width=\"1.5\" stroke-dasharray=\"8,4\"/>")
            append("<text x=\"${(cx + sc + 5).s()}\" y=\"${(cy - 5).s()}\" font-size=\"11\" fill=\"#E63B2E\" font-weight=\"700\" font-family=\"Inter,sans-serif\">1 km</text>")
            // impact dots
            for (d in impacts) {
                val (px, py) = toPixel(centerLat, centerLon, d.lat, d.lon, cx, cy, sc)
                if (px < 2 || px > size - 2 || py < 2 || py > size - 2) continue
                val c = if (d.isPositive) "#E63B2E" else "#2B7FE0"
                append("<circle cx=\"${px.s()}\" cy=\"${py.s()}\" r=\"5\" fill=\"$c\" opacity=\"0.8\" stroke=\"#fff\" stroke-width=\"1\"/>")
            }
            // center crosshair (study point)
            append("<line x1=\"${(cx - 10).s()}\" y1=\"${cy.s()}\" x2=\"${(cx + 10).s()}\" y2=\"${cy.s()}\" stroke=\"#1A2B4A\" stroke-width=\"2.5\"/>")
            append("<line x1=\"${cx.s()}\" y1=\"${(cy - 10).s()}\" x2=\"${cx.s()}\" y2=\"${(cy + 10).s()}\" stroke=\"#1A2B4A\" stroke-width=\"2.5\"/>")
            append("<circle cx=\"${cx.s()}\" cy=\"${cy.s()}\" r=\"3.5\" fill=\"#1A2B4A\"/>")
            // legend
            val ly = size - 42.0
            append("<rect x=\"10\" y=\"${(ly - 6).s()}\" width=\"160\" height=\"40\" rx=\"6\" fill=\"rgba(255,255,255,0.92)\" stroke=\"#E0E6F0\"/>")
            append("<circle cx=\"22\" cy=\"${(ly + 8).s()}\" r=\"5\" fill=\"#E63B2E\"/>")
            append("<text x=\"32\" y=\"${(ly + 12).s()}\" font-size=\"11\" fill=\"#4A5568\" font-family=\"Inter,sans-serif\">${labels.cgPositive}</text>")
            append("<circle cx=\"22\" cy=\"${(ly + 24).s()}\" r=\"5\" fill=\"#2B7FE0\"/>")
            append("<text x=\"32\" y=\"${(ly + 28).s()}\" font-size=\"11\" fill=\"#4A5568\" font-family=\"Inter,sans-serif\">${labels.cgNegative}</text>")
            // counts
            append("<text x=\"${(size - 12.0).s()}\" y=\"${(size - 14.0).s()}\" text-anchor=\"end\" font-size=\"10\" fill=\"#6B7A99\" font-family=\"Inter,sans-serif\">&lt;1 km : $less1Km \u00b7 1\u20132 km : $less2Km</text>")
            append("</svg>")
        }
    }

    /**
     * NSG grid map — study point at center with quadrant cross.
     * Shows project name or GPS label + lat/lon grid lines.
     */
    fun nsgGridMap(
        centerLat: Double,
        centerLon: Double,
        projectName: String = "",
        size: Int = 460,
        viewKm: Double = 5.0,
        lang: PdfOutputLang = PdfOutputLang.FR
    ): String {
        val cx = size / 2.0
        val cy = size / 2.0
        val label = projectName.ifBlank { "${centerLat.fmt(4)}, ${centerLon.fmt(4)}" }
        val safeName = label.take(40).replace("&", "&amp;").replace("<", "&lt;")
            .replace(">", "&gt;").replace("\"", "&quot;")
        return buildString {
            append("<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 $size $size\" ")
            append("style=\"width:100%;max-width:${size / 3}px;display:block;margin:14px auto\">")
            append("<rect width=\"$size\" height=\"$size\" fill=\"#FAFBFC\" rx=\"10\"/>")
            append(MapTileUtils.svgTileImages(centerLat, centerLon, viewKm, size))
            // quadrant cross — dashed through center
            append("<line x1=\"${cx.s()}\" y1=\"0\" x2=\"${cx.s()}\" y2=\"$size\" stroke=\"#2B7FE0\" stroke-width=\"1.5\" stroke-dasharray=\"8,4\" opacity=\"0.5\"/>")
            append("<line x1=\"0\" y1=\"${cy.s()}\" x2=\"$size\" y2=\"${cy.s()}\" stroke=\"#2B7FE0\" stroke-width=\"1.5\" stroke-dasharray=\"8,4\" opacity=\"0.5\"/>")
            // center study point
            append("<circle cx=\"${cx.s()}\" cy=\"${cy.s()}\" r=\"8\" fill=\"#2B7FE0\" stroke=\"#fff\" stroke-width=\"2\"/>")
            append("<circle cx=\"${cx.s()}\" cy=\"${cy.s()}\" r=\"3\" fill=\"#fff\"/>")
            // label
            append("<text x=\"${(cx + 14).s()}\" y=\"${(cy - 14).s()}\" font-size=\"13\" fill=\"#1A2B4A\" font-weight=\"700\" font-family=\"Inter,sans-serif\">$safeName</text>")
            // coordinates
            append("<text x=\"${(cx + 14).s()}\" y=\"${(cy - 2).s()}\" font-size=\"11\" fill=\"#6B7A99\" font-family=\"Inter,sans-serif\">${centerLat.fmt(4)}, ${centerLon.fmt(4)}</text>")
            // quadrant labels
            val offset = 20.0
            val west = if (lang == PdfOutputLang.EN) "W" else "O"
            append("<text x=\"${offset.s()}\" y=\"${offset.s()}\" font-size=\"11\" fill=\"#6B7A99\" font-family=\"Inter,sans-serif\">N$west</text>")
            append("<text x=\"${(size - offset).s()}\" y=\"${offset.s()}\" text-anchor=\"end\" font-size=\"11\" fill=\"#6B7A99\" font-family=\"Inter,sans-serif\">NE</text>")
            append("<text x=\"${offset.s()}\" y=\"${(size - 12.0).s()}\" font-size=\"11\" fill=\"#6B7A99\" font-family=\"Inter,sans-serif\">S$west</text>")
            append("<text x=\"${(size - offset).s()}\" y=\"${(size - 12.0).s()}\" text-anchor=\"end\" font-size=\"11\" fill=\"#6B7A99\" font-family=\"Inter,sans-serif\">SE</text>")
            append("</svg>")
        }
    }

    /**
     * Impact distance map — site marker + impact marker + dashed line with distance label.
     * Used by the lightning certificate (cert foudroiement).
     */
    fun impactDistanceMap(
        siteLat: Double,
        siteLon: Double,
        impactLat: Double,
        impactLon: Double,
        distLabel: String,
        size: Int = 460,
        lang: PdfOutputLang = PdfOutputLang.FR
    ): String {
        val cx = size / 2.0
        val cy = size / 2.0
        val siteLabel = PdfTranslations.get(lang).site
        val impactLabel = when (lang) {
            PdfOutputLang.FR -> "Impact"
            PdfOutputLang.EN -> "Strike"
            PdfOutputLang.ES, PdfOutputLang.PT -> "Impacto"
        }
        val midLat = (siteLat + impactLat) / 2.0
        val midLon = (siteLon + impactLon) / 2.0
        val dLat = abs(siteLat - impactLat) * KM_PER_DEG_LAT
        val dLon = abs(siteLon - impactLon) * KM_PER_DEG_LAT * cos(midLat * PI / 180.0)
        val extent = maxOf(dLat, dLon).coerceAtLeast(0.2) * 1.8
        val sc = cx / extent
        val (sx, sy) = toPixel(midLat, midLon, siteLat, siteLon, cx, cy, sc)
        val (ix, iy) = toPixel(midLat, midLon, impactLat, impactLon, cx, cy, sc)
        val mx = ((sx + ix) / 2).s()
        val my = ((sy + iy) / 2 - 14).s()
        return buildString {
            append("<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 $size $size\" ")
            append("style=\"width:100%;max-width:${size / 3}px;display:block;margin:14px auto\">")
            append("<rect width=\"$size\" height=\"$size\" fill=\"#FAFBFC\" rx=\"10\"/>")
            append(MapTileUtils.svgTileImages(midLat, midLon, extent, size))
            // dashed line between site and impact
            append("<line x1=\"${sx.s()}\" y1=\"${sy.s()}\" x2=\"${ix.s()}\" y2=\"${iy.s()}\" stroke=\"#888\" stroke-width=\"1.5\" stroke-dasharray=\"6,4\"/>")
            // distance label at midpoint
            append("<text x=\"$mx\" y=\"$my\" text-anchor=\"middle\" font-size=\"13\" fill=\"#1A2B4A\" font-weight=\"700\" font-family=\"Inter,sans-serif\">$distLabel</text>")
            // site marker (blue circle with S)
            append("<circle cx=\"${sx.s()}\" cy=\"${sy.s()}\" r=\"12\" fill=\"#2B7FE0\" stroke=\"#fff\" stroke-width=\"2\"/>")
            append("<text x=\"${sx.s()}\" y=\"${(sy + 1).s()}\" text-anchor=\"middle\" dominant-baseline=\"central\" font-size=\"10\" fill=\"#fff\" font-weight=\"700\" font-family=\"Inter,sans-serif\">S</text>")
            // impact marker (red circle with lightning)
            append("<circle cx=\"${ix.s()}\" cy=\"${iy.s()}\" r=\"12\" fill=\"#E63B2E\" stroke=\"#fff\" stroke-width=\"2\"/>")
            append("<text x=\"${ix.s()}\" y=\"${(iy + 1).s()}\" text-anchor=\"middle\" dominant-baseline=\"central\" font-size=\"12\" fill=\"#fff\">\u26A1</text>")
            // legend
            val ly = size - 42.0
            append("<rect x=\"10\" y=\"${(ly - 6).s()}\" width=\"130\" height=\"40\" rx=\"6\" fill=\"rgba(255,255,255,0.92)\" stroke=\"#E0E6F0\"/>")
            append("<circle cx=\"22\" cy=\"${(ly + 8).s()}\" r=\"6\" fill=\"#2B7FE0\"/>")
            append("<text x=\"22\" y=\"${(ly + 8.5).s()}\" text-anchor=\"middle\" dominant-baseline=\"central\" font-size=\"8\" fill=\"#fff\" font-weight=\"700\">S</text>")
            append("<text x=\"34\" y=\"${(ly + 12).s()}\" font-size=\"11\" fill=\"#4A5568\" font-family=\"Inter,sans-serif\">$siteLabel</text>")
            append("<circle cx=\"22\" cy=\"${(ly + 24).s()}\" r=\"6\" fill=\"#E63B2E\"/>")
            append("<text x=\"22\" y=\"${(ly + 24.5).s()}\" text-anchor=\"middle\" dominant-baseline=\"central\" font-size=\"9\" fill=\"#fff\">\u26A1</text>")
            append("<text x=\"34\" y=\"${(ly + 28).s()}\" font-size=\"11\" fill=\"#4A5568\" font-family=\"Inter,sans-serif\">$impactLabel</text>")
            append("</svg>")
        }
    }
}
